import { useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Target, Zap, BarChart3, Users, CheckCircle, ArrowRight, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import accompagnementMontreal from "@/assets/accompagnement-montreal.jpg";

declare global {
  interface Window {
    Cal?: any;
  }
}

const initCalEmbed = () => {
  if (typeof window !== "undefined" && !window.Cal) {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) {
        a.q.push(ar);
      };
      let d = C.document;
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal;
          let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "premier-contact-accompagnement-strategique", { origin: "https://app.cal.com" });
    window.Cal.ns["premier-contact-accompagnement-strategique"]("ui", { hideEventTypeDetails: false, layout: "month_view" });
  }
};

const AccompagnementMontreal = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    initCalEmbed();
  }, []);

  const openCalendar = () => {
    if (window.Cal) {
      window.Cal.ns["premier-contact-accompagnement-strategique"]("openModal", {
        calLink: "maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique",
      });
    }
  };

  const forWho = [
    "Entrepreneurs à Montréal",
    "PME montréalaises",
    "Marques personnelles",
    "Entreprises B2B",
  ];

  const notForWho = [
    "Ceux qui veulent des résultats sans stratégie",
    "Ceux qui cherchent seulement des \"posts\"",
    "Ceux qui veulent le prix le plus bas",
  ];

  const steps = [
    { num: "1", title: "Appel stratégique", desc: "On discute de votre situation" },
    { num: "2", title: "Analyse", desc: "Évaluation de votre situation actuelle" },
    { num: "3", title: "Stratégie", desc: "Définition du plan d'action" },
    { num: "4", title: "Mise en place", desc: "Contenu et publicité déployés" },
    { num: "5", title: "Optimisation", desc: "Amélioration continue" },
  ];

  const whyUs = [
    "Une approche stratégique, pas générique",
    "Une compréhension du marché local",
    "Un focus sur la génération de leads",
    "Une relation de partenariat, pas de sous-traitance",
  ];

  return (
    <>
      <SEOHead
        title="Accompagnement marketing à Montréal | Génération de leads – Agence 80/20"
        description="Agence 80/20 offre un accompagnement marketing à Montréal pour entrepreneurs et PME qui veulent générer des leads grâce au contenu stratégique et à la publicité payante."
        canonical="/accompagnement-marketing-montreal"
      />

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={accompagnementMontreal} 
              alt="Accompagnement marketing à Montréal" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <MapPin className="h-4 w-4" />
                <span className="text-sm font-medium">Montréal</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Accompagnement marketing à Montréal pour <span className="text-primary">générer des leads</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Agence 80/20 accompagne des entrepreneurs et PME à Montréal qui veulent structurer leur marketing afin de générer des leads qualifiés grâce au contenu stratégique et à la publicité payante.
              </p>
              
              <p className="text-lg text-foreground font-medium mb-8">
                L'objectif n'est pas d'être visible partout. L'objectif est d'être visible auprès des bonnes personnes, au bon moment.
              </p>
              
              <Button 
                size="lg" 
                onClick={openCalendar}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Réserver un appel stratégique
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Montreal Context Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Un accompagnement marketing pensé pour le marché montréalais
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Montréal est un marché unique :
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {["Forte concurrence", "Entrepreneurs très sollicités", "Audience exposée à beaucoup de marketing"].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-background p-4 rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-muted-foreground text-center mb-4">
                Pour se démarquer à Montréal, il ne suffit pas de publier du contenu. Il faut une stratégie claire, adaptée au marché local.
              </p>
              
              <p className="text-lg text-foreground font-medium text-center">
                C'est exactement ce que propose Agence 80/20 : un accompagnement marketing aligné avec la réalité des entreprises montréalaises.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Les problèmes marketing fréquents des entreprises à Montréal
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Beaucoup d'entrepreneurs à Montréal vivent les mêmes enjeux :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "Du contenu qui fait des vues mais peu de ventes",
                  "Des publicités payantes coûteuses et peu rentables",
                  "Aucune cohérence entre l'organique et le payant",
                  "Peu de leads réellement qualifiés"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-destructive/5 p-4 rounded-lg border border-destructive/20">
                    <span className="text-destructive font-bold">✕</span>
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-foreground font-medium text-center">
                Sans structure, les efforts marketing deviennent inefficaces.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
                L'approche Agence 80/20 à Montréal
              </h2>
              
              <p className="text-lg text-muted-foreground mb-12 text-center">
                Notre accompagnement marketing à Montréal repose sur une approche simple et performante.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Clarifier votre positionnement</h3>
                  <p className="text-muted-foreground mb-4">On définit :</p>
                  <ul className="space-y-2">
                    {["Votre client idéal à Montréal", "Votre message principal", "Ce qui vous différencie localement"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Créer du contenu stratégique</h3>
                  <p className="text-muted-foreground mb-4">Du contenu conçu pour :</p>
                  <ul className="space-y-2">
                    {["Attirer l'attention", "Éduquer votre audience", "Créer de la confiance"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-4 text-sm">Chaque contenu a un objectif précis dans le parcours du prospect.</p>
                </div>
                
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Accélérer avec la publicité payante</h3>
                  <p className="text-muted-foreground mb-4">La publicité payante permet :</p>
                  <ul className="space-y-2">
                    {["De rejoindre rapidement votre audience", "De tester les messages", "De générer des leads plus rapidement"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-4 text-sm">Le contenu nourrit la publicité, et la publicité amplifie le contenu.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* For Who Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                À qui s'adresse cet accompagnement marketing à Montréal
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-primary/5 p-6 rounded-xl border border-primary/20">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-primary" />
                    C'est conçu pour :
                  </h3>
                  <ul className="space-y-3">
                    {forWho.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-muted/50 p-6 rounded-xl border border-border">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <span className="text-muted-foreground">✕</span>
                    Ce n'est pas pour :
                  </h3>
                  <ul className="space-y-3">
                    {notForWho.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-muted-foreground">
                        <span className="text-muted-foreground">✕</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Pourquoi choisir Agence 80/20 comme agence marketing à Montréal
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Choisir une agence marketing à Montréal, c'est avant tout une question de compréhension du terrain.
              </p>
              
              <p className="text-lg text-foreground font-medium mb-6 text-center">
                Agence 80/20 se distingue par :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {whyUs.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-background p-4 rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                Comment débute l'accompagnement marketing
              </h2>
              
              <div className="grid md:grid-cols-5 gap-4">
                {steps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                      {step.num}
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-muted-foreground text-center mt-8">
                Chaque étape est orientée vers des résultats concrets.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Prêt à générer plus de leads à Montréal ?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                Si vous êtes un entrepreneur ou une PME à Montréal et que vous voulez structurer votre marketing pour générer des leads qualifiés de façon constante, cet accompagnement est conçu pour vous.
              </p>
              
              <Button 
                size="lg" 
                onClick={openCalendar}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Réserver un appel stratégique
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
                Articles et pages reliés
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Link 
                  to="/accompagnement-marketing-quebec"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing au Québec</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs et PME partout au Québec.</p>
                </Link>
                
                <Link 
                  to="/accompagnement-marketing-laval"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing à Laval</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs et PME à Laval.</p>
                </Link>
                
                <Link 
                  to="/accompagnement-marketing-rive-sud"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing sur la Rive-Sud</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs et PME sur la Rive-Sud.</p>
                </Link>
                
                <Link 
                  to="/articles/accompagnement-marketing-generer-des-leads"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing : générer des leads</h3>
                  <p className="text-sm text-muted-foreground">Découvrez comment un accompagnement structuré permet de générer des leads.</p>
                </Link>
                
                <Link 
                  to="/articles/strategie-de-contenu-ventes"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Stratégie de contenu : transformer l'attention en ventes</h3>
                  <p className="text-sm text-muted-foreground">Comment une stratégie de contenu bien structurée génère des ventes.</p>
                </Link>
                
                <Link 
                  to="/accompagnement"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing – Postuler</h3>
                  <p className="text-sm text-muted-foreground">Découvre notre processus de sélection et postule pour l'accompagnement.</p>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AccompagnementMontreal;
